import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import type { RootState } from '@/app/providers/store';
import type {
  ConversationItemType,
  PersonItemType,
} from '@/features/chat/types/conversation';

import {
  type FollowUpState,
  type ProfileState,
  deriveConversationState,
  deriveCurrentError,
  deriveNormalizedConversationMessages,
} from '../helpers/derive-conversation-state';
import { isSharedConversationId } from '../helpers/shared-conversation';
import type {
  MessageErrorType,
  MessageItemType,
  MessageListType,
} from '../types/message';

export interface NormalizedConversationMessages {
  profileMessage?: MessageItemType;
  followUpMessages: MessageItemType[];
}

interface ChatStore {
  activeConversation: ConversationItemType | undefined;
  messages: MessageListType;
  profile: ProfileState | null;
  followUp: FollowUpState | null;
  isStaticPopularSearchStreaming: boolean;
  isSendingMessage: boolean;
  isSaved: boolean;
  isAnswering: boolean;
  showFindWithAi: boolean;
  preSearch: {
    isLoading: boolean;
    data: {
      foundedPersons: PersonItemType[];
      currentPerson: PersonItemType;
    } | null;
    error: MessageErrorType | null;
    selectedPersonId: string | undefined;
    showPersonList: boolean;
  };
}

const initialState: ChatStore = {
  activeConversation: undefined,
  messages: [],
  profile: null,
  followUp: null,
  isStaticPopularSearchStreaming: false,
  isSendingMessage: false,
  isSaved: false,
  isAnswering: false,
  showFindWithAi: false,
  preSearch: {
    isLoading: false,
    data: null,
    error: null,
    selectedPersonId: undefined,
    showPersonList: false,
  },
};

const syncThreadState = (state: ChatStore): void => {
  const { profile, followUp } = deriveConversationState(state.messages);
  state.profile = profile;
  state.followUp = followUp;
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setActiveConversation: (
      state,
      action: PayloadAction<ChatStore['activeConversation']>,
    ) => {
      const previousConversationId = state.activeConversation?.id;
      const newConversationId = action.payload?.id;
      const didConversationChange =
        previousConversationId !== newConversationId;
      const isPromotingSharedConversation =
        isSharedConversationId(previousConversationId) &&
        newConversationId !== undefined;
      const shouldResetConversationMessages =
        didConversationChange &&
        previousConversationId !== undefined &&
        !isPromotingSharedConversation;

      state.activeConversation = action.payload;
      state.isSaved = action.payload?.savedPerson ?? false;

      if (shouldResetConversationMessages) {
        state.messages = [];
        state.isAnswering = false;
        state.isStaticPopularSearchStreaming = false;
      }

      if (didConversationChange && newConversationId !== undefined) {
        state.preSearch.showPersonList = false;
        state.showFindWithAi = false;
      }

      syncThreadState(state);
    },
    setMessages: (state, action: PayloadAction<ChatStore['messages']>) => {
      state.messages = action.payload;
      syncThreadState(state);
    },
    setMessage: (state, action: PayloadAction<MessageItemType>) => {
      const index = state.messages.findIndex(
        (msg) => msg.id === action.payload.id,
      );
      if (index !== -1) {
        state.messages[index] = action.payload;
      } else {
        state.messages.push(action.payload);
      }
      syncThreadState(state);
    },
    removeMessage: (state, action: PayloadAction<MessageItemType['id']>) => {
      state.messages = state.messages.filter(
        (msg) => msg.id !== action.payload,
      );
      syncThreadState(state);
    },
    addChunk: (
      state,
      action: PayloadAction<{
        id: MessageItemType['id'];
        delta: {
          content?: string;
          links?: MessageItemType['links'];
          images?: MessageItemType['images'];
          relatedQuestions?: MessageItemType['relatedQuestions'];
          socialMediaAccounts?: MessageItemType['socialMediaAccounts'];
          digitalInformation?: MessageItemType['digitalInformation'];
        };
      }>,
    ) => {
      const { id, delta } = action.payload;
      const message = state.messages.find((msg) => msg.id === id);
      if (message !== undefined) {
        if (delta.content !== undefined) {
          message.content = (message.content ?? '') + delta.content;
        }
        if (delta.links !== undefined) {
          message.links = delta.links;
        }
        if (delta.images !== undefined) {
          message.images = delta.images;
        }
        if (delta.relatedQuestions !== undefined) {
          message.relatedQuestions = delta.relatedQuestions;
        }
        if (delta.socialMediaAccounts !== undefined) {
          message.socialMediaAccounts = delta.socialMediaAccounts;
        }
        if (delta.digitalInformation !== undefined) {
          message.digitalInformation = delta.digitalInformation;
        }
        message.error = undefined;
      }
      syncThreadState(state);
    },
    resetMessages: (state) => {
      state.messages = [];
      syncThreadState(state);
      state.isAnswering = false;
      state.isStaticPopularSearchStreaming = false;
      state.showFindWithAi = false;
    },
    clearAllMessages: (state) => {
      state.messages = [];
      syncThreadState(state);
      state.isStaticPopularSearchStreaming = false;
    },
    resetPreSearch: (state) => {
      state.showFindWithAi = false;
      state.preSearch = {
        ...state.preSearch,
        isLoading: false,
        data: null,
        error: null,
        selectedPersonId: undefined,
        showPersonList: false,
      };
    },
    setPreSearchError: (
      state,
      action: PayloadAction<MessageErrorType | null>,
    ) => {
      state.preSearch.error = action.payload;
    },
    setIsSendingMessage: (state, action: PayloadAction<boolean>) => {
      state.isSendingMessage = action.payload;
    },
    setIsSaved: (state, action: PayloadAction<boolean>) => {
      state.isSaved = action.payload;
    },
    setIsAnswering: (state, action: PayloadAction<boolean>) => {
      state.isAnswering = action.payload;
    },
    setIsStaticPopularSearchStreaming: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isStaticPopularSearchStreaming = action.payload;
    },
    setPreSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.preSearch.isLoading = action.payload;
    },
    setPreSearchData: (
      state,
      action: PayloadAction<ChatStore['preSearch']['data']>,
    ) => {
      state.preSearch.data = action.payload;
    },
    setSelectedPersonId: (state, action: PayloadAction<string | undefined>) => {
      state.preSearch.selectedPersonId = action.payload;
    },
    setShowPersonList: (state, action: PayloadAction<boolean>) => {
      state.preSearch.showPersonList = action.payload;
    },
    setShowFindWithAi: (state, action: PayloadAction<boolean>) => {
      state.showFindWithAi = action.payload;
    },
    setPreSearchSuccess: (
      state,
      action: PayloadAction<{
        foundedPersons: PersonItemType[];
        currentPerson: PersonItemType;
      }>,
    ) => {
      state.preSearch.isLoading = false;
      state.preSearch.error = null;
      state.preSearch.data = action.payload;
    },
  },
});

export const {
  setActiveConversation,
  setMessages,
  setMessage,
  removeMessage,
  addChunk,
  resetMessages,
  clearAllMessages,
  resetPreSearch,
  setIsSendingMessage,
  setIsSaved,
  setIsAnswering,
  setIsStaticPopularSearchStreaming,
  setPreSearchLoading,
  setPreSearchData,
  setSelectedPersonId,
  setShowPersonList,
  setShowFindWithAi,
  setPreSearchSuccess,
  setPreSearchError,
} = chatSlice.actions;

export const selectChat = (state: RootState): ChatStore => state.chat;
export const selectActiveConversation = (
  state: RootState,
): ConversationItemType | null | undefined => state.chat.activeConversation;
export const selectMessages = (state: RootState): MessageItemType[] =>
  state.chat.messages;
export const selectProfile = (state: RootState): ProfileState | null =>
  state.chat.profile;
export const selectFollowUp = (state: RootState): FollowUpState | null =>
  state.chat.followUp;
export const selectNormalizedConversationMessages = createSelector(
  [selectMessages],
  (messages): NormalizedConversationMessages =>
    deriveNormalizedConversationMessages(messages),
);
export const selectCurrentError = createSelector(
  [selectProfile, selectFollowUp],
  (profile, followUp): MessageErrorType | null =>
    deriveCurrentError(profile, followUp),
);
export const selectIsSendingMessage = (state: RootState): boolean =>
  state.chat.isSendingMessage;
export const selectIsSaved = (state: RootState): boolean => state.chat.isSaved;
export const selectIsAnswering = (state: RootState): boolean =>
  state.chat.isAnswering;
export const selectIsStaticPopularSearchStreaming = (
  state: RootState,
): boolean => state.chat.isStaticPopularSearchStreaming;

export const selectPreSearch = (state: RootState): ChatStore['preSearch'] =>
  state.chat.preSearch;
const EMPTY_PERSONS: PersonItemType[] = [];

export const selectPreSearchPersons = createSelector(
  [(state: RootState) => state.chat.preSearch.data?.foundedPersons],
  (foundedPersons): PersonItemType[] => foundedPersons ?? EMPTY_PERSONS,
);
export const selectPreSearchCurrentPerson = (
  state: RootState,
): PersonItemType | null => state.chat.preSearch.data?.currentPerson ?? null;
export const selectSelectedPersonId = (state: RootState): string | undefined =>
  state.chat.preSearch.selectedPersonId;
export const selectPreSearchLoading = (state: RootState): boolean =>
  state.chat.preSearch.isLoading;
export const selectShowPersonList = (state: RootState): boolean =>
  state.chat.preSearch.showPersonList;
export const selectPreSearchError = (
  state: RootState,
): MessageErrorType | null => state.chat.preSearch.error;
export const selectShowFindWithAi = (state: RootState): boolean =>
  state.chat.showFindWithAi;

export const chatReducer = chatSlice.reducer;
